import {
  QBankStructureLevelCategory,
  QConfigStructureType,
  useGetQBankStructureLevelItemChildrenByIdLazyQuery,
  useGetQBankStructureLevelsByQBankStructureIdLazyQuery,
} from '@exam-management/graphql';
import { useEffect, useMemo, useState } from 'react';

import { type QConfigStructure, type LevelItemWithLevel } from '../../types';

function findQbsIdByChapterId(item: QConfigStructure, structures: QConfigStructure[]) {
  if (item.type === QConfigStructureType.QBankStructure) {
    return item.refId;
  } else {
    const parent = structures.find((structure) => structure.id === item.parentId);
    if (parent) {
      return findQbsIdByChapterId(parent, structures);
    }
  }
}

interface UseGetLevelItemsWithLevelInput {
  chapterData: QConfigStructure | undefined;
  structures: QConfigStructure[];
}

export function useGetLevelItemsWithLevel(input: Readonly<UseGetLevelItemsWithLevelInput>) {
  const { chapterData, structures } = input;

  const [levelItemsWithLevel, setLevelItemsWithLevel] = useState<LevelItemWithLevel[]>([]);

  const [getLevelItemChildren, { loading: isGettingLevelItemChildren }] =
    useGetQBankStructureLevelItemChildrenByIdLazyQuery();
  const [getLevels, { loading: isGettingLevel }] =
    useGetQBankStructureLevelsByQBankStructureIdLazyQuery();

  const qbsId = useMemo(() => {
    if (!chapterData) return null;
    return findQbsIdByChapterId(chapterData, structures) ?? null;
  }, [chapterData, structures]);

  useEffect(() => {
    async function getData(qbsId: string, chapterRefId: string) {
      const [{ data: levelItemChildrenResult }, { data: levelsResult }] = await Promise.all([
        getLevelItemChildren({
          variables: { id: chapterRefId },
          fetchPolicy: 'network-only',
        }),
        getLevels({
          variables: { qBankStructureId: qbsId },
          fetchPolicy: 'network-only',
        }),
      ]);

      const levelItemChildren =
        levelItemChildrenResult?.qbsGetQBankStructureLevelItemChildrenById ?? [];
      const levels = levelsResult?.qbsGetQBankStructureLevelsByQBankStructureId ?? [];

      const levelMapById = new Map(levels.map((level) => [level.id, level]));

      const itemsWithLevel = levelItemChildren
        .map((levelItem) => {
          const level = levelMapById.get(levelItem.qBankStructureLevelId);

          if (!level) {
            return null;
          }

          return {
            ...levelItem,
            level,
          };
        })
        .filter((item): item is LevelItemWithLevel => item !== null);

      if (chapterData?.refId && levels.length > 0) {
        const chapterLevel = levels.find((l) => l.category === QBankStructureLevelCategory.Chapter);

        if (chapterLevel) {
          itemsWithLevel.push({
            __typename: 'QBankStructureLevelItem',
            id: chapterData.refId,
            parentLevelItemId: null,
            order: chapterData.order,
            tag: null,
            qBankStructureLevelId: chapterLevel.id,
            level: chapterLevel,
          });
        }
      }

      setLevelItemsWithLevel(itemsWithLevel);
    }

    if (!chapterData?.refId || !qbsId) return;
    getData(qbsId, chapterData.refId);
  }, [chapterData, qbsId, getLevelItemChildren, getLevels]);

  const isLoading =
    isGettingLevelItemChildren || isGettingLevel || levelItemsWithLevel.length === 0;

  return { levelItemsWithLevel, isLoading };
}
